import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icon';

// ClickUp tarzı görev listesi: gruplu (durum / marka / kişi), açılıp kapanan gruplar,
// satırdan durum-kişi-tarih-öncelik değiştirme ve grubun altından hızlı görev ekleme.

// Görev kategorisi (ClickUp'taki marka içi "Liste"); backend utils/taskCategory.js ile aynı anahtarlar
export const CATEGORY_MAP = {
  reklam: { label: 'Reklam / PPC', color: '#7DB4FF' },
  listing: { label: 'Listing / İçerik', color: '#A78BFA' },
  stok: { label: 'Stok / Lojistik', color: '#F2B84B' },
  rapor: { label: 'Raporlama / Analiz', color: '#34D399' },
  musteri: { label: 'Müşteri tarafı', color: '#FF8A3D' },
  ic: { label: 'İç iş', color: '#9A9A92' },
};
export const CATEGORY_OPTIONS = Object.entries(CATEGORY_MAP);

const AVATAR_COLORS = ['#7DB4FF', '#A78BFA', '#FF8A3D', '#34D399', '#F2B84B', '#FF6B5B', '#D4FF3A'];
export const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toLocaleUpperCase('tr');
export const avatarColor = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];

export function Avatar({ user, size = 22 }) {
  return (
    <span className="tl-av" title={user.name} style={{ width: size, height: size, fontSize: size * 0.42, background: avatarColor(user.id) + '33', color: avatarColor(user.id) }}>
      {initials(user.name)}
    </span>
  );
}

// Tıklanınca açılan küçük menü; dışarı tıklayınca kapanır
export function Pop({ open, onClose, children, align = 'left', width = 220 }) {
  if (!open) return null;
  return (
    <>
      <div className="tl-pop__shield" onClick={(e) => { e.stopPropagation(); onClose(); }} />
      <div className="tl-pop" style={{ [align]: 0, width }} onClick={(e) => e.stopPropagation()}>{children}</div>
    </>
  );
}

// Markasız görev için marka seçici (arama kutulu)
function BrandPicker({ accounts, onPick, onClose }) {
  const [q, setQ] = useState('');
  const n = (x) => String(x || '').toLocaleLowerCase('tr');
  const list = accounts.filter(a => !q || n(a.name).includes(n(q))).slice(0, 50);
  return (
    <Pop open onClose={onClose} width={240}>
      <input className="tl-pop__search" autoFocus placeholder="Marka ara…" value={q} onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && list[0]) onPick(list[0].id); if (e.key === 'Escape') onClose(); }} />
      <div className="tl-pop__scroll">
        {list.map(a => <button key={a.id} type="button" className="tl-pop__item" onClick={() => onPick(a.id)}>{a.name}</button>)}
        {list.length === 0 && <div className="text-muted" style={{ padding: 8, fontSize: 12 }}>Marka bulunamadı</div>}
      </div>
    </Pop>
  );
}

const assigneesOf = (t) => (Array.isArray(t.assignees) && t.assignees.length ? t.assignees
  : t.assigned_to ? [{ id: t.assigned_to, name: t.assigned_name || '?' }] : []);

export function dueInfo(d) {
  if (!d) return null;
  const date = new Date(d); const now = new Date();
  now.setHours(0, 0, 0, 0); date.setHours(0, 0, 0, 0);
  const diff = Math.round((date - now) / 86400000);
  if (diff < 0) return { label: `${Math.abs(diff)} gün gecikti`, color: 'var(--red)' };
  if (diff === 0) return { label: 'Bugün', color: 'var(--red)' };
  if (diff === 1) return { label: 'Yarın', color: 'var(--yellow)' };
  if (diff < 7) return { label: `${diff} gün`, color: 'var(--yellow)' };
  return { label: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }), color: 'var(--text-soft)' };
}

function Row({ task, statusMap, statuses, priorityMap, users, accounts, onUpdate, onOpen, onPlay, selected, hideBrand, hideCategory, checked, onCheck }) {
  const [menu, setMenu] = useState(null); // 'status' | 'assignee' | 'priority' | 'due'
  const st = statusMap[task.status] || statusMap.todo;
  const people = assigneesOf(task);
  const due = dueInfo(task.due_date);
  const done = task.status === 'done';
  const pr = priorityMap[task.priority];
  const close = () => setMenu(null);
  // Tarih klavyeyle yazılırken yarım değer kaydedilmesin: Enter veya alan dışına çıkınca kaydet
  const commitDue = (v) => {
    close();
    const cur = task.due_date ? String(task.due_date).slice(0, 10) : '';
    if (v !== cur && (!v || /^\d{4}-\d{2}-\d{2}$/.test(v)) && (!v || v >= '2000-01-01')) onUpdate(task.id, { due_date: v || null });
  };
  const toggleAssignee = (uid) => {
    const ids = people.map(p => p.id);
    onUpdate(task.id, { assignee_ids: ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid] });
  };

  return (
    <div className={`tl-row ${selected ? 'tl-row--sel' : ''} ${done ? 'tl-row--done' : ''}`} onClick={() => onOpen(task)}>
      <span className="tl-cell tl-cell--status" onClick={(e) => e.stopPropagation()}>
        {onCheck && (
          <input type="checkbox" className="tl-check" checked={!!checked} onChange={(e) => onCheck(task.id, e.target.checked)} title="Toplu marka ataması için seç" />
        )}
        <button type="button" className="tl-dot" title={`Durum: ${st.label}`} onClick={() => setMenu('status')}
          style={{ borderColor: st.color, background: done ? st.color : 'transparent' }}>
          {done && <Icon name="check" size={9} />}
        </button>
        <Pop open={menu === 'status'} onClose={close}>
          {statuses.map(([k, v]) => (
            <button key={k} type="button" className={`tl-pop__item ${k === task.status ? 'is-on' : ''}`}
              onClick={() => { close(); if (k !== task.status) onUpdate(task.id, { status: k }); }}>
              <span className="tl-sq" style={{ background: v.color }} />{v.label}
            </button>
          ))}
        </Pop>
      </span>

      <span className="tl-cell tl-cell--title">
        <span className="tl-title">{task.title}</span>
        {!hideBrand && (task.account_name || task.company) && <span className="tl-brand">{task.account_name || task.company}</span>}
        {!task.account_id && accounts && accounts.length > 0 && (
          <span style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="tl-brand tl-brand--add" onClick={() => setMenu('brand')} title="Bu görevi bir markaya bağla">+ Marka</button>
            {menu === 'brand' && <BrandPicker accounts={accounts} onClose={close} onPick={(id) => { close(); onUpdate(task.id, { account_id: id }); }} />}
          </span>
        )}
        {!hideCategory && CATEGORY_MAP[task.category] && (
          <span className="tl-cat" title="Kategori"><span className="tl-sq" style={{ background: CATEGORY_MAP[task.category].color }} />{CATEGORY_MAP[task.category].label}</span>
        )}
        {task.approval_required && <span className="tl-tag" style={{ color: 'var(--yellow)' }}>Onay gerekli</span>}
        {task.checklist_total > 0 && (
          <span className="tl-meta" title="Kontrol listesi" style={task.checklist_done === task.checklist_total ? { color: 'var(--green)' } : null}>
            <Icon name="squareCheck" size={12} />{task.checklist_done}/{task.checklist_total}
          </span>
        )}
        {task.comment_count > 0 && <span className="tl-meta" title="Yorum"><Icon name="note" size={12} />{task.comment_count}</span>}
        {task.source_meeting && (
          <button type="button" className="tl-src" title={`Toplantıdan: ${task.source_meeting.title || ''} — kayıtta bu ana git`}
            onClick={(e) => { e.stopPropagation(); onPlay(task.source_meeting.meeting_id, task.source_meeting.ts_seconds); }}>
            <Icon name="play" size={9} /> toplantı
          </button>
        )}
      </span>

      <span className="tl-cell tl-cell--people" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tl-people" onClick={() => setMenu('assignee')} title={people.map(p => p.name).join(', ') || 'Kişi ata'}>
          {people.length === 0 ? <span className="tl-av tl-av--empty"><Icon name="user" size={11} /></span>
            : people.slice(0, 3).map(p => <Avatar key={p.id} user={p} />)}
          {people.length > 3 && <span className="tl-more">+{people.length - 3}</span>}
        </button>
        <Pop open={menu === 'assignee'} onClose={close} align="right" width={240}>
          <div className="tl-pop__head">Atanan kişiler</div>
          <div className="tl-pop__scroll">
            {users.map(u => {
              const on = people.some(p => p.id === u.id);
              return (
                <button key={u.id} type="button" className={`tl-pop__item ${on ? 'is-on' : ''}`} onClick={() => toggleAssignee(u.id)}>
                  <Avatar user={u} size={18} /> <span style={{ flex: 1 }}>{u.name}</span>{on && <Icon name="check" size={12} />}
                </button>
              );
            })}
          </div>
        </Pop>
      </span>

      <span className="tl-cell tl-cell--due" onClick={(e) => e.stopPropagation()}>
        {menu === 'due' ? (
          <input type="date" className="tl-date" autoFocus defaultValue={task.due_date ? String(task.due_date).slice(0, 10) : ''}
            onBlur={(e) => commitDue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') close(); }} />
        ) : (
          <button type="button" className="tl-due" onClick={() => setMenu('due')} style={{ color: done ? 'var(--text-muted)' : due?.color }}>
            {due ? due.label : <span className="tl-ghost"><Icon name="calendar" size={13} /></span>}
          </button>
        )}
        {menu !== 'due' && task.due_date && (
          <button type="button" className="tl-x" title="Tarihi kaldır" onClick={() => onUpdate(task.id, { due_date: null })}>
            <Icon name="x" size={10} />
          </button>
        )}
      </span>

      <span className="tl-cell tl-cell--prio" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tl-flag" title={`Öncelik: ${pr?.label || '—'}`} onClick={() => setMenu('priority')} style={{ color: pr?.color }}>
          <Icon name="flag" size={14} />
        </button>
        <Pop open={menu === 'priority'} onClose={close} align="right" width={150}>
          {Object.entries(priorityMap).map(([k, v]) => (
            <button key={k} type="button" className={`tl-pop__item ${k === task.priority ? 'is-on' : ''}`}
              onClick={() => { close(); if (k !== task.priority) onUpdate(task.id, { priority: k }); }}>
              <span style={{ color: v.color, display: 'inline-flex' }}><Icon name="flag" size={13} /></span>{v.label}
            </button>
          ))}
        </Pop>
      </span>
    </div>
  );
}

function QuickAdd({ onCreate }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (open && ref.current) ref.current.focus(); }, [open, busy]);
  const submit = async () => {
    const title = text.trim();
    if (!title || busy) return;
    setBusy(true);
    try { await onCreate(title); setText(''); } catch (e) { /* hata api katmanında gösterilir */ }
    setBusy(false);
  };
  if (!open) {
    return <button type="button" className="tl-add" onClick={() => setOpen(true)}><Icon name="plus" size={12} /> Görev ekle</button>;
  }
  return (
    <div className="tl-add tl-add--open">
      <input ref={ref} className="tl-add__input" value={text} disabled={busy} maxLength={200}
        placeholder="Görev adı yaz, Enter ile kaydet (Esc: kapat)"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') { setOpen(false); setText(''); } }}
        onBlur={() => { if (!text.trim()) setOpen(false); }} />
      <button type="button" className="btn btn--primary" style={{ padding: '3px 12px', fontSize: 12 }} onMouseDown={(e) => e.preventDefault()} onClick={submit} disabled={busy || !text.trim()}>Kaydet</button>
    </div>
  );
}

// Grupları oluştur. Kişiye göre gruplamada çok kişili görev her kişinin altında görünür (ClickUp gibi).
function buildGroups(tasks, groupBy, statuses, users) {
  if (groupBy === 'account') {
    const map = new Map();
    tasks.forEach(t => {
      const name = t.account_name || t.company || '';
      const key = t.account_id ? `a${t.account_id}` : name ? `c${name.toLocaleLowerCase('tr')}` : 'none';
      if (!map.has(key)) map.set(key, { key, label: !name ? 'Markasız (iç iş)' : t.account_id ? name : `${name} · markaya bağlı değil`, color: 'var(--text-soft)', defaults: t.account_id ? { account_id: t.account_id } : name ? { company: name } : {}, tasks: [] });
      map.get(key).tasks.push(t);
    });
    return [...map.values()].sort((a, b) => (a.key === 'none') - (b.key === 'none') || a.label.localeCompare(b.label, 'tr'));
  }
  if (groupBy === 'category') {
    return [...CATEGORY_OPTIONS, [null, { label: 'Diğer (kategorisiz)', color: '#55554F' }]]
      .map(([k, v]) => ({ key: k || 'none', label: v.label, color: v.color, defaults: k ? { category: k } : {}, tasks: tasks.filter(t => (t.category || null) === k) }));
  }
  if (groupBy === 'assignee') {
    const map = new Map();
    tasks.forEach(t => {
      const people = assigneesOf(t);
      (people.length ? people : [null]).forEach(p => {
        const key = p ? `u${p.id}` : 'none';
        if (!map.has(key)) {
          const u = p && (users.find(x => x.id === p.id) || p);
          map.set(key, { key, label: u ? u.name : 'Atanmamış', user: u, color: u ? avatarColor(u.id) : 'var(--text-muted)', defaults: u ? { assignee_ids: [u.id] } : {}, tasks: [] });
        }
        map.get(key).tasks.push(t);
      });
    });
    return [...map.values()].sort((a, b) => (a.key === 'none') - (b.key === 'none') || a.label.localeCompare(b.label, 'tr'));
  }
  return statuses.map(([k, v]) => ({ key: k, label: v.label, color: v.color, defaults: { status: k }, tasks: tasks.filter(t => t.status === k) }));
}

export default function TaskList({ tasks, users, accounts, groupBy, subGroupBy, statuses, statusMap, priorityMap, onUpdate, onCreate, onOpen, onPlay, onBulkBrand, selectedId }) {
  // Markasız görevler için toplu marka ataması
  const [picked, setPicked] = useState(() => new Set());
  const [bulkBrand, setBulkBrand] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const onCheck = (id, on) => setPicked(p => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });
  const pickedIds = [...picked].filter(id => tasks.some(t => t.id === id && !t.account_id));
  const applyBulk = async () => {
    if (!bulkBrand || !pickedIds.length) return;
    setBulkBusy(true);
    try { await onBulkBrand(pickedIds, Number(bulkBrand)); setPicked(new Set()); setBulkBrand(''); } finally { setBulkBusy(false); }
  };
  // Alt gruplama (ClickUp "Subgroup"): ör. durum içinde marka marka
  const sub = subGroupBy && subGroupBy !== groupBy ? subGroupBy : null;
  const storeKey = `tasksCollapsed:${groupBy}:${sub || '-'}`;
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(storeKey) || 'null'); } catch (e) { /* önemsiz */ }
    setCollapsed(saved || (groupBy === 'status' ? { done: true } : {}));
  }, [storeKey, groupBy]);
  const toggle = (key, def = false) => setCollapsed(c => {
    const next = { ...c, [key]: !(key in c ? c[key] : def) };
    try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch (e) { /* önemsiz */ }
    return next;
  });

  const groups = buildGroups(tasks, groupBy, statuses, users);
  const hideBrand = groupBy === 'account' || sub === 'account';
  const hideCategory = groupBy === 'category' || sub === 'category';
  const rows = (list, key) => list.map(t => (
    <Row key={`${key}-${t.id}`} task={t} statusMap={statusMap} statuses={statuses} priorityMap={priorityMap}
      users={users} accounts={accounts} onUpdate={onUpdate} onOpen={onOpen} onPlay={onPlay} selected={selectedId === t.id} hideBrand={hideBrand} hideCategory={hideCategory}
      checked={picked.has(t.id)} onCheck={!t.account_id && onBulkBrand ? onCheck : null} />
  ));
  const head = (
    <div className="tl-row tl-row--head">
      <span /><span>Görev</span><span>Atanan</span><span>Bitiş</span><span>Öncelik</span>
    </div>
  );
  const subLabel = (sg) => (sub === 'status'
    ? <span className="tl-pill tl-pill--sm" style={{ background: sg.color, color: '#0E0F11' }}>{sg.label}</span>
    : sub === 'category'
      ? <span className="tl-sub__name"><span className="tl-sq" style={{ background: sg.color }} />{sg.label}</span>
    : sub === 'assignee' && sg.user
      ? <span className="tl-sub__name"><Avatar user={sg.user} size={18} />{sg.label}</span>
      : <span className="tl-sub__name">{sg.label}</span>);
  const visible = groupBy === 'status' ? groups : groups.filter(g => g.tasks.length);

  return (
    <div className="tl">
      {pickedIds.length > 0 && (
        <div className="tl-bulk">
          <strong>{pickedIds.length} markasız görev seçildi</strong>
          <select className="form-input" value={bulkBrand} onChange={(e) => setBulkBrand(e.target.value)} style={{ width: 'auto', padding: '4px 10px', fontSize: 13 }}>
            <option value="">Marka seç…</option>
            {(accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button type="button" className="btn btn--primary" style={{ padding: '4px 14px', fontSize: 13 }} disabled={!bulkBrand || bulkBusy} onClick={applyBulk}>
            {bulkBusy ? 'Atanıyor…' : 'Markaya ata'}
          </button>
          <button type="button" className="btn btn--secondary" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setPicked(new Set())}>Vazgeç</button>
        </div>
      )}
      {visible.length === 0 && <div className="card text-muted" style={{ padding: 32, textAlign: 'center' }}>Görev bulunamadı</div>}
      {visible.map(g => {
        const isClosed = !!collapsed[g.key];
        return (
          <section key={g.key} className="tl-group">
            <header className="tl-group__head" onClick={() => toggle(g.key)}>
              <span className="tl-chev"><Icon name={isClosed ? 'chevronRight' : 'chevronDown'} size={14} /></span>
              {groupBy === 'status'
                ? <span className="tl-pill" style={{ background: g.color, color: '#0E0F11' }}>{g.label}</span>
                : groupBy === 'category'
                  ? <span className="tl-group__name"><span className="tl-sq" style={{ background: g.color }} />{g.label}</span>
                : groupBy === 'assignee' && g.user
                  ? <span className="tl-group__name"><Avatar user={g.user} size={20} />{g.label}</span>
                  : <span className="tl-group__name">{g.label}</span>}
              <span className="tl-count">{g.tasks.length}</span>
            </header>
            {!isClosed && !sub && (
              <div className="tl-body">
                {g.tasks.length > 0 && head}
                {rows(g.tasks, g.key)}
                <QuickAdd onCreate={(title) => onCreate({ title, ...g.defaults })} />
              </div>
            )}
            {!isClosed && sub && (
              <div className="tl-body">
                {g.tasks.length > 0 && head}
                {buildGroups(g.tasks, sub, statuses, users).filter(sg => sg.tasks.length).map(sg => {
                  const k = `${g.key}|${sg.key}`;
                  // Tamamlananlar alt grubu, kullanıcı açmadıkça kapalı
                  const subClosed = k in collapsed ? !!collapsed[k] : (sub === 'status' && sg.key === 'done');
                  return (
                    <div key={k} className="tl-sub">
                      <div className="tl-sub__head" onClick={() => toggle(k, sub === 'status' && sg.key === 'done')}>
                        <span className="tl-chev"><Icon name={subClosed ? 'chevronRight' : 'chevronDown'} size={12} /></span>
                        {subLabel(sg)}
                        <span className="tl-count">{sg.tasks.length}</span>
                      </div>
                      {!subClosed && rows(sg.tasks, k)}
                      {!subClosed && <QuickAdd onCreate={(title) => onCreate({ title, ...g.defaults, ...sg.defaults })} />}
                    </div>
                  );
                })}
                {g.tasks.length === 0 && <QuickAdd onCreate={(title) => onCreate({ title, ...g.defaults })} />}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
